import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import {
  BULK_SCOPE,
  getBulkDisplayCount,
  getBulkScopeLabel,
} from "../../helper/bulkSelection/bulkSelectionModes";

/**
 * Shared V2 bulk action bar (Targeting / Search Term / Placement Bids).
 *
 * @param {Array<{ key: string, label: string, variant?: 'contained'|'outlined', color?: string }>} actions
 */
export default function BulkActionBarV2({
  open = false,
  scope = BULK_SCOPE.SELECTED,
  selectedIdsCount = 0,
  totalCount = 0,
  excludeIdsCount = 0,
  showSelectAllMatching = false,
  onSelectAllMatching,
  onClear,
  actions = [],
  onAction,
}) {
  if (!open) return null;

  const displayCount = getBulkDisplayCount({
    scope,
    selectedIdsCount,
    totalCount,
    excludeIdsCount,
  });
  const scopeLabel = getBulkScopeLabel({
    scope,
    selectedIdsCount,
    totalCount,
    excludeIdsCount,
  });

  const isAllMatching =
    scope === BULK_SCOPE.ALL_MATCHING ||
    scope === BULK_SCOPE.ALL_MATCHING_EXCEPT;

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 0.5,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        border: "1px solid",
        borderColor: isAllMatching ? "primary.light" : "grey.300",
        background: isAllMatching
          ? "linear-gradient(90deg, #F7F5FF 0%, #FFFFFF 55%)"
          : "#FFFFFF",
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={1.25}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
        >
          <IconButton
            size="small"
            onClick={onClear}
            aria-label="Clear selection"
            sx={{
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 1,
              width: 32,
              height: 32,
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18, color: "#333" }} />
          </IconButton>

          <Chip
            icon={
              <CheckCircleOutlineRoundedIcon
                sx={{ fontSize: "16px !important" }}
              />
            }
            label={`${displayCount.toLocaleString()} selected`}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "12px",
              bgcolor: isAllMatching ? "#EDE7FF" : "#F3F4F6",
              color: "#333",
              "& .MuiChip-icon": { color: isAllMatching ? "#6B4EFF" : "#666" },
            }}
          />

          <Typography
            variant="body2"
            sx={{ fontSize: "12px", color: "text.secondary" }}
          >
            {scopeLabel}
          </Typography>

          {showSelectAllMatching ? (
            <Button
              size="small"
              onClick={onSelectAllMatching}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 600,
                color: "#6B4EFF",
                px: 1,
                minHeight: 28,
                "&:hover": { bgcolor: "#F3EFFF" },
              }}
            >
              Select all {Number(totalCount).toLocaleString()} matching filters
            </Button>
          ) : null}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" }, mx: 0.5 }}
          />
          {actions.map((action) => (
            <Button
              key={action.key}
              size="small"
              variant={action.variant || "outlined"}
              color={action.color || "inherit"}
              onClick={() => onAction?.(action.key)}
              startIcon={action.startIcon || null}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 600,
                height: 32,
                borderColor: "grey.400",
                color: action.variant === "contained" ? undefined : "#333",
                bgcolor:
                  action.variant === "contained" ? undefined : "transparent",
                "&:hover": {
                  borderColor: "grey.700",
                  bgcolor:
                    action.variant === "contained" ? undefined : "grey.50",
                },
              }}
            >
              {action.label}
            </Button>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}
